import { useState } from 'react';
import { Code2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/ui/utils';

/**
 * VersionBadge - Displays app version and build info
 * 
 * Shows version number, build timestamp, and environment.
 * Click to toggle detailed view.
 * 
 * @example
 * ```tsx
 * <VersionBadge />
 * ```
 */
export function VersionBadge() {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get version from package.json (injected via Vite)
  const version = '1.0.0';
  
  // Build timestamp (injected via Vite at build time)
  const buildTime = import.meta.env.VITE_BUILD_TIME || new Date().toISOString();
  
  // Environment
  const isDev = import.meta.env.DEV;
  const env = isDev ? 'dev' : 'prod';

  // Format build date
  const buildDate = new Date(buildTime);
  const formattedDate = buildDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const formattedTime = buildDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <button
      onClick={() => setIsExpanded(!isExpanded)}
      className={cn(
        'fixed bottom-20 right-4 z-40',
        'md:bottom-4 md:right-4',
        'transition-all duration-300 ease-in-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'rounded-md'
      )}
      aria-label={isExpanded ? 'Ocultar detalhes da versão' : 'Mostrar detalhes da versão'}
      title="Clique para ver detalhes"
    >
      <Badge
        variant={isDev ? 'default' : 'secondary'}
        className={cn(
          'transition-all duration-300',
          'text-xs font-mono',
          'cursor-pointer hover:scale-105',
          isDev && 'bg-amber-500 hover:bg-amber-600 text-white',
          !isDev && 'bg-muted hover:bg-muted/80'
        )}
      >
        <Code2 className="w-3 h-3 mr-1" />
        {isExpanded ? (
          <span className="flex flex-col items-start gap-0.5">
            <span className="font-semibold">v{version} ({env})</span>
            <span className="text-[10px] opacity-80">{formattedDate} {formattedTime}</span>
          </span>
        ) : (
          <span>v{version}</span>
        )}
      </Badge>
    </button>
  );
}

